import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  Car, 
  Users, 
  Clock, 
  DollarSign, 
  Star,
  Plus,
  Bell,
  TrendingUp,
  MapPin,
  Calendar,
  Phone,
  MessageCircle,
  CheckCircle,
  XCircle,
  AlertCircle
} from 'lucide-react';
import WeatherWidget from '../components/WeatherWidget';
import RideStatistics from '../components/RideStatistics';
import QuickActions from '../components/QuickActions';
import RazorpayButton from '../components/RazorpayButton';
import { fetchWithAuth } from '../services/fetchWithAuth';

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [activeRides, setActiveRides] = useState([]);
  const [connectedRides, setConnectedRides] = useState([]);
  const [showNotification, setShowNotification] = useState(false);
  const [connectPacks, setConnectPacks] = useState([]);
  const [selectedPack, setSelectedPack] = useState(null);
  const [buying, setBuying] = useState(false);
  const [buySuccess, setBuySuccess] = useState(false);
  const [buyError, setBuyError] = useState('');
  const [connects, setConnects] = useState(user?.connects || 0);

  // Simulate real-time updates
  useEffect(() => {
    const interval = setInterval(() => {
      // Simulate new notifications
      if (Math.random() > 0.7) {
        const newNotification = {
          id: Date.now(),
          type: 'ride_request',
          message: 'New ride request from nearby user',
          time: 'Just now',
          read: false
        };
        setNotifications(prev => [newNotification, ...prev.slice(0, 4)]);
        setShowNotification(true);
        setTimeout(() => setShowNotification(false), 3000);
      }
    }, 10000);

    return () => clearInterval(interval);
  }, []);

  // Load connected rides from localStorage
  useEffect(() => {
    const savedConnectedRides = localStorage.getItem('connectedRides');
    if (savedConnectedRides) {
      setConnectedRides(JSON.parse(savedConnectedRides));
    }
  }, []);

  useEffect(() => {
    async function fetchPacks() {
      try {
        const res = await fetchWithAuth('/api/connects/packs');
        const data = await res.json();
        setConnectPacks(data.packs || []);
        setConnects(data.currentBalance || user?.connects || 0);
      } catch (e) {
        setConnectPacks([]);
      }
    }
    fetchPacks();
  }, [user]);

  const handleChat = (connectId) => {
    navigate(`/chat/${connectId}`);
  };

  const stats = [
    {
      title: 'Total Rides',
      value: '24',
      icon: Car,
      color: 'bg-blue-500',
      change: '+12%'
    },
    {
      title: 'Connects Used',
      value: `${user?.connects || 0}`,
      icon: Users,
      color: 'bg-green-500',
      change: '-3'
    },
    {
      title: 'Money Saved',
      value: '₹2,450',
      icon: DollarSign,
      color: 'bg-purple-500',
      change: '+₹180'
    },
    {
      title: 'Rating',
      value: '4.8',
      icon: Star,
      color: 'bg-yellow-500',
      change: '+0.2'
    }
  ];

  const quickActions = [
    {
      title: 'Share a Ride',
      description: 'Create a new ride announcement',
      icon: Plus,
      color: 'bg-blue-600',
      link: '/create-announcement'
    },
    {
      title: 'Find Connects',
      description: 'Browse premium connects',
      icon: Car,
      color: 'bg-green-600',
      link: '/connects'
    },
    {
      title: 'Connect Requests',
      description: 'Manage ride requests',
      icon: Bell,
      color: 'bg-purple-600',
      link: '/connect-requests'
    },
    {
      title: 'Ride History',
      description: 'View past rides',
      icon: Clock,
      color: 'bg-orange-600',
      link: '/ride-history'
    }
  ];

  const handleAcceptRide = (rideId) => {
    setActiveRides(prev => prev.map(ride => 
      ride.id === rideId ? { ...ride, status: 'accepted' } : ride
    ));
  };

  const handleRejectRide = (rideId) => {
    setActiveRides(prev => prev.filter(ride => ride.id !== rideId));
  };

  const markNotificationRead = (notificationId) => {
    setNotifications(prev => prev.map(notif => 
      notif.id === notificationId ? { ...notif, read: true } : notif
    ));
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Notification Toast */}
      {showNotification && (
        <div className="fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 animate-bounce">
          <div className="flex items-center">
            <Bell className="w-5 h-5 mr-2" />
            <span>New notification received!</span>
          </div>
        </div>
      )}

      {/* Welcome Section */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">
          Welcome back, {user?.name || 'User'}! 👋
        </h1>
        <p className="text-gray-600 mt-2">
          Here's what's happening with your rides today.
        </p>
      </div>

      {/* Weather and Quick Actions Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <div className="lg:col-span-2">
          <QuickActions />
        </div>
        <div>
          <WeatherWidget />
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div key={index} className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                  <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                </div>
                <div className={`${stat.color} p-3 rounded-lg`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
              </div>
              <div className="mt-4 flex items-center">
                <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
                <span className="text-sm text-green-600">{stat.change}</span>
                <span className="text-sm text-gray-500 ml-1">from last month</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Razorpay Payment Section */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Buy Connects</h2>
        <div className="bg-white rounded-lg shadow p-6 flex flex-col items-center">
          <p className="mb-4 text-gray-700">Select a connect pack and pay securely with Razorpay.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 w-full">
            {connectPacks.map((pack) => (
              <div
                key={pack.id}
                className={`border rounded-lg p-4 cursor-pointer w-full ${selectedPack?.id === pack.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}
                onClick={() => setSelectedPack(pack)}
              >
                <h4 className="font-semibold text-lg mb-2">{pack.name}</h4>
                <div className="text-2xl font-bold text-blue-600 mb-1">₹{pack.price}</div>
                <div className="text-gray-600 mb-1">{pack.connects} Connects</div>
                {pack.popular && <span className="text-xs bg-blue-500 text-white px-2 py-1 rounded">Popular</span>}
                <div className="text-xs text-gray-500 mt-2">{pack.description}</div>
              </div>
            ))}
          </div>
          {selectedPack && (
            <RazorpayButton
              amount={selectedPack.price}
              receipt={`connect_pack_${selectedPack.id}_${Date.now()}`}
              onSuccess={async (response) => {
                setBuying(true);
                setBuyError('');
                try {
                  // Verify payment with backend
                  const verifyRes = await fetchWithAuth('/api/payments/verify', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      orderId: response.razorpay_order_id,
                      paymentId: response.razorpay_payment_id,
                      signature: response.razorpay_signature
                    })
                  });
                  const verifyData = await verifyRes.json();
                  if (verifyRes.ok) {
                    setBuySuccess(true);
                    setConnects((c) => c + (verifyData.connectsAdded || selectedPack.connects));
                  } else {
                    setBuyError(verifyData.error || 'Payment verification failed');
                  }
                } catch (e) {
                  setBuyError('Payment verification failed');
                } finally {
                  setBuying(false);
                }
              }}
              onFailure={() => setBuyError('Payment failed or cancelled')}
            />
          )}
          {buying && <div className="mt-2 text-blue-600">Processing payment...</div>}
          {buySuccess && <div className="mt-2 text-green-600">Connects added successfully!</div>}
          {buyError && <div className="mt-2 text-red-600">{buyError}</div>}
        </div>
      </div>

      {/* Connected Rides Section */}
      {connectedRides.length > 0 && (
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Your Connected Rides</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {connectedRides.map((ride) => (
              <div key={ride.id} className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <img
                      src={ride.user.avatar}
                      alt={ride.user.name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <h3 className="font-semibold text-gray-900">{ride.user.name}</h3>
                      <div className="flex items-center space-x-2">
                        <Star className="w-4 h-4 text-yellow-400 fill-current" />
                        <span className="text-sm text-gray-600">{ride.user.rating}</span>
                      </div>
                    </div>
                  </div>
                  <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                    Connected
                  </span>
                </div>
                
                <div className="mb-3">
                  <div className="flex items-center space-x-2 mb-1">
                    <MapPin className="w-4 h-4 text-green-500" />
                    <span className="text-sm font-medium">{ride.ride.from}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <MapPin className="w-4 h-4 text-red-500" />
                    <span className="text-sm font-medium">{ride.ride.to}</span>
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4 text-sm text-gray-600">
                    <div className="flex items-center space-x-1">
                      <Calendar className="w-3 h-3" />
                      <span>{ride.ride.date}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Clock className="w-3 h-3" />
                      <span>{ride.ride.time}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => handleChat(ride.id)}
                    className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2"
                  >
                    <MessageCircle className="w-4 h-4" />
                    <span>Chat</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}



      {/* Active Rides Section */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Active Rides</h2>
        <div className="bg-white rounded-lg shadow">
          {activeRides.length > 0 ? (
            <div className="divide-y divide-gray-200">
              {activeRides.map((ride) => (
                <div key={ride.id} className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <img
                        src={ride.user.avatar}
                        alt={ride.user.name}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                      <div>
                        <h3 className="font-semibold text-gray-900">{ride.user.name}</h3>
                        <p className="text-sm text-gray-600">{ride.from} → {ride.to}</p>
                        <p className="text-sm text-gray-500">{ride.date} at {ride.time}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {ride.status === 'pending' && (
                        <>
                          <button
                            onClick={() => handleAcceptRide(ride.id)}
                            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2"
                          >
                            <CheckCircle className="w-4 h-4" />
                            <span>Accept</span>
                          </button>
                          <button
                            onClick={() => handleRejectRide(ride.id)}
                            className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors flex items-center space-x-2"
                          >
                            <XCircle className="w-4 h-4" />
                            <span>Reject</span>
                          </button>
                        </>
                      )}
                      {ride.status === 'accepted' && (
                        <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm">
                          Accepted
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center">
              <Car className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No active rides</h3>
              <p className="text-gray-600 mb-4">Start sharing rides to see them here.</p>
              <Link
                to="/create-announcement"
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Share a Ride
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Ride Statistics */}
      <div className="mb-8">
        <RideStatistics />
      </div>
    </div>
  );
};

export default Dashboard; 